// src/pages/admin/CourseLectures/lectures/Lecture04AnschreibenDIN5008.tsx
import React, { useState, useRef } from "react";
import {
  Checklist,
  InfoBox,
  AgendaCard,
  TaskList,
} from "../components/LectureUI";

// ── Shared Accordion ──────────────────────────────────────────────
const Accordion: React.FC<{
  id: string;
  num: string;
  icon: string;
  title: string;
  openId: string;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}> = ({ id, num, icon, title, openId, onToggle, children }) => {
  const isOpen = openId === id;
  const ref = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    onToggle(id);
    if (!isOpen) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div
      ref={ref}
      className={`mb-3 rounded-xl border overflow-hidden shadow-sm ${isOpen ? "border-[#009fe3]/50" : "border-gray-200"} bg-white`}
    >
      <button
        onClick={handleToggle}
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${isOpen ? "bg-blue-50" : "hover:bg-gray-50"}`}
      >
        <div
          className={`flex-shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-colors ${isOpen ? "bg-[#009fe3] text-white" : "bg-gray-100 text-gray-500"}`}
        >
          <span>{icon}</span>
          <span className="hidden sm:inline">{num}</span>
        </div>
        <span
          className={`flex-1 text-sm font-bold ${isOpen ? "text-[#0369a1]" : "text-gray-800"}`}
        >
          {title}
        </span>
        <span
          className={`flex-shrink-0 text-[#009fe3] transition-transform duration-200 text-sm ${isOpen ? "rotate-180" : ""}`}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// ── Mock Anschreiben letter ───────────────────────────────────────
const MockAnschreiben: React.FC = () => (
  <div className="border-2 border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm font-sans text-[11px] sm:text-xs leading-relaxed">
    {/* Header bar */}
    <div className="bg-[#009fe3] px-4 py-2 flex items-center justify-between">
      <span className="text-white font-bold text-xs">
        Beispiel — Anschreiben nach DIN 5008
      </span>
      <span className="text-blue-100 text-[10px]">
        Max. 1 Seite · 150–300 Wörter
      </span>
    </div>
    <div className="p-4 sm:p-5 space-y-3">
      {/* Briefkopf */}
      <div className="border-l-2 border-[#009fe3] pl-3">
        <div className="text-[10px] font-bold text-[#009fe3] uppercase tracking-wide mb-1">
          ① Briefkopf (Absender)
        </div>
        <div className="text-gray-700">
          <p className="font-semibold">Max Mustermann</p>
          <p>Musterstraße 1 · 10115 Berlin</p>
          <p>📞 +49 123 456789 · ✉️ max.mustermann@gmail.com</p>
        </div>
      </div>

      {/* Empfänger */}
      <div className="border-l-2 border-blue-300 pl-3">
        <div className="text-[10px] font-bold text-[#009fe3] uppercase tracking-wide mb-1">
          ② Empfänger
        </div>
        <div className="text-gray-700">
          <p className="font-semibold">Musterfirma GmbH & Co. KG</p>
          <p>z. Hd. Frau Müller</p>
          <p>Firmenstraße 10 · 20095 Hamburg</p>
        </div>
      </div>

      {/* Datum */}
      <div className="flex justify-end border-l-2 border-blue-200 pl-3">
        <div>
          <div className="text-[10px] font-bold text-[#009fe3] uppercase tracking-wide mb-1">
            ③ Datum (rechtsbündig)
          </div>
          <p className="text-gray-700 text-right">Berlin, 20. Februar 2026</p>
        </div>
      </div>

      {/* Betreff */}
      <div className="border-l-2 border-amber-300 pl-3">
        <div className="text-[10px] font-bold text-[#009fe3] uppercase tracking-wide mb-1">
          ④ Betreffzeile (fett, ohne „Betreff:")
        </div>
        <p className="font-bold text-gray-800">
          Bewerbung um einen Ausbildungsplatz als Kaufmann im Einzelhandel
          (Ausbildungsbeginn 01.09.2026)
        </p>
      </div>

      {/* Anrede */}
      <div className="border-l-2 border-green-300 pl-3">
        <div className="text-[10px] font-bold text-[#009fe3] uppercase tracking-wide mb-1">
          ⑤ Anrede (immer persönlich)
        </div>
        <p className="text-gray-700">Sehr geehrte Frau Müller,</p>
      </div>

      {/* Hauptteil */}
      <div className="border-l-2 border-purple-300 pl-3 space-y-2">
        <div className="text-[10px] font-bold text-[#009fe3] uppercase tracking-wide mb-1">
          ⑥ Hauptteil (150–300 Wörter · klare Absätze)
        </div>

        <div className="bg-purple-50 rounded px-2 py-1.5">
          <span className="text-[9px] font-bold text-purple-500 uppercase">
            Einleitung — Hook
          </span>
          <p className="text-gray-700 mt-0.5">
            Seit meiner Schulzeit fasziniert mich der Handel: Wie Produkte ihren
            Weg zum Kunden finden und wie man Vertrauen aufbaut, das hat mich
            immer begeistert. Deshalb bewerbe ich mich mit großer Motivation für
            die Ausbildung als Kaufmann im Einzelhandel bei der Musterfirma
            GmbH.
          </p>
        </div>

        <div className="bg-blue-50 rounded px-2 py-1.5">
          <span className="text-[9px] font-bold text-blue-500 uppercase">
            Stärken & Fähigkeiten (XYZ)
          </span>
          <p className="text-gray-700 mt-0.5">
            In meiner Schulzeit habe ich gelernt, Aufgaben selbstständig zu
            organisieren (X), indem ich stets strukturierte Lernpläne erstellt
            habe (Y), sodass ich meinen Bachelor mit einem GPA von 3,8
            abgeschlossen habe (Z). Meine Computerkenntnisse in MS Office sowie
            meine Deutschkenntnisse auf B2-Niveau ermöglichen mir eine
            professionelle Kommunikation.
          </p>
        </div>

        <div className="bg-green-50 rounded px-2 py-1.5">
          <span className="text-[9px] font-bold text-green-600 uppercase">
            Warum dieses Unternehmen?
          </span>
          <p className="text-gray-700 mt-0.5">
            Die Musterfirma GmbH überzeugt mich durch ihre nachhaltige
            Unternehmensphilosophie und ihr starkes Kundenserviceversprechen.
            Ich bin überzeugt, dass meine Lernbereitschaft und Zuverlässigkeit
            gut zu Ihren Werten passen.
          </p>
        </div>

        <div className="bg-amber-50 rounded px-2 py-1.5">
          <span className="text-[9px] font-bold text-amber-600 uppercase">
            Call to Action
          </span>
          <p className="text-gray-700 mt-0.5">
            Ich freue mich sehr über die Möglichkeit, mich in einem persönlichen
            Gespräch vorzustellen. Bitte beachten Sie, dass ich mich aktuell
            noch aus Bangladesch bewerbe und ab dem 01.07.2026 in Deutschland
            verfügbar sein werde.
          </p>
        </div>
      </div>

      {/* Schluss */}
      <div className="border-l-2 border-gray-300 pl-3">
        <div className="text-[10px] font-bold text-[#009fe3] uppercase tracking-wide mb-1">
          ⑦ Schluss
        </div>
        <p className="text-gray-700">Mit freundlichen Grüßen</p>
        <p className="mt-3 font-semibold text-gray-800">Max Mustermann</p>
        <p className="text-gray-400 text-[10px] mt-1 italic">
          Anlagen: Lebenslauf · Zeugnisse mit Übersetzungen
        </p>
      </div>
    </div>
  </div>
);

// ── Dos & Don'ts Table ────────────────────────────────────────────
const DosAndDonts: React.FC = () => {
  const rows = [
    {
      do: "Individuell an jede Stelle anpassen",
      dont: "Das gleiche Anschreiben für alle nutzen",
    },
    {
      do: "Schlüsselwörter aus der Anzeige nutzen",
      dont: "Den Lebenslauf einfach nacherzählen",
    },
    {
      do: "Kurz und konkret bleiben (150–300 Wörter)",
      dont: "Leere Floskeln oder Klischees",
    },
    {
      do: "Lücken / Karrierewechsel kurz erklären",
      dont: "Sich für Schwächen entschuldigen",
    },
  ];
  return (
    <div className="rounded-xl overflow-hidden border border-gray-200">
      <div className="grid grid-cols-2">
        <div className="bg-green-600 px-3 py-2 text-white text-xs font-bold text-center">
          ✓ Do (Machen)
        </div>
        <div className="bg-red-600 px-3 py-2 text-white text-xs font-bold text-center">
          ✕ Don't (Vermeiden)
        </div>
      </div>
      {rows.map((row, i) => (
        <div
          key={i}
          className={`grid grid-cols-2 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
        >
          <div className="px-3 py-2.5 border-r border-gray-200 flex items-start gap-2 text-xs text-gray-700">
            <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">
              ✓
            </span>
            {row.do}
          </div>
          <div className="px-3 py-2.5 flex items-start gap-2 text-xs text-gray-700">
            <span className="text-red-500 font-bold flex-shrink-0 mt-0.5">
              ✕
            </span>
            {row.dont}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────
const Lecture04AnschreibenDIN5008: React.FC = () => {
  const [openSection, setOpenSection] = useState<string>("");
  const toggle = (id: string) =>
    setOpenSection((prev) => (prev === id ? "" : id));

  return (
    <div>
      {/* Recap */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 mb-4">
        <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">
          📋 Aufgaben aus Lektion 03 — erledigt?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            "3 Berufe & Stellenanzeigen ausgesucht",
            "Keywords markiert & verstanden",
            "3 Lebensläufe nach Keywords vorbereitet",
            "XYZ-Regel & STAR-Methode angewendet",
            "Fokus auf B2-Sprachniveau & Sprachzeugnis",
          ].map((t, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 text-xs text-amber-800"
            >
              <span className="w-4 h-4 rounded border border-amber-400 flex-shrink-0 bg-white" />
              {t}
            </div>
          ))}
        </div>
      </div>

      <AgendaCard
        items={[
          { num: "1", label: "Was & Warum" },
          { num: "2", label: "Struktur (DIN 5008)" },
          { num: "3", label: "Der Hauptteil" },
          { num: "4", label: "Einleitung — Hook" },
          { num: "5", label: "Show, don't tell" },
          { num: "6", label: "Warum dieses Unternehmen?" },
          { num: "7", label: "Erfolgsfaktoren" },
          { num: "8", label: "Dos & Don'ts" },
          { num: "9", label: "Checkliste vor Absenden" },
          { num: "10", label: "Beispiel Anschreiben" },
        ]}
      />

      {/* 01 */}
      <Accordion
        id="01"
        num="ABSCHNITT 01"
        icon="📄"
        title="Was ist ein Anschreiben & warum ist es wichtig?"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          {[
            {
              icon: "📝",
              label: "Definition",
              desc: "Kurzes, formelles Dokument (150–300 Wörter) — erklärt deine Motivation und Passgenauigkeit",
              color: "bg-blue-50 border-blue-200 text-blue-800",
            },
            {
              icon: "🚀",
              label: "Zweck",
              desc: "Deine Chance, dich abzuheben — besonders bei Lücken im Lebenslauf oder Karrierewechsel",
              color: "bg-purple-50 border-purple-200 text-purple-800",
            },
            {
              icon: "🎯",
              label: "Ziel",
              desc: "Neugier wecken und eine Einladung zum Vorstellungsgespräch erhalten",
              color: "bg-green-50 border-green-200 text-green-800",
            },
          ].map((item, i) => (
            <div key={i} className={`rounded-xl border p-3 ${item.color}`}>
              <div className="text-2xl mb-1">{item.icon}</div>
              <p className="text-sm font-bold mb-1">{item.label}</p>
              <p className="text-xs opacity-90">{item.desc}</p>
            </div>
          ))}
        </div>
        <InfoBox variant="tip" icon="💡" title="Merksatz">
          Das Anschreiben ist keine Formalität — es ist deine persönliche
          Einladungskarte zum Gespräch.
        </InfoBox>
      </Accordion>

      {/* 02 */}
      <Accordion
        id="02"
        num="ABSCHNITT 02"
        icon="📐"
        title="Struktur — Der formale Rahmen (DIN 5008)"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-3">
          Ein Anschreiben nach DIN 5008 hat immer diese 7 Bestandteile — in
          dieser Reihenfolge:
        </p>
        <div className="space-y-2">
          {[
            {
              num: "①",
              label: "Briefkopf",
              desc: "Dein Name, Anschrift, Telefon, E-Mail",
              color: "border-[#009fe3] bg-blue-50",
            },
            {
              num: "②",
              label: "Empfänger",
              desc: "Firmenname (GmbH & Co. etc.), Ansprechpartner, Adresse",
              color: "border-blue-300 bg-blue-50/50",
            },
            {
              num: "③",
              label: "Datum",
              desc: "Rechtsbündig oder in der Zeile des Ortes",
              color: "border-indigo-300 bg-indigo-50",
            },
            {
              num: "④",
              label: "Betreffzeile",
              desc: 'Fett gedruckt, OHNE das Wort „Betreff:". Nenne die genaue Ausbildungsstelle.',
              color: "border-amber-300 bg-amber-50",
            },
            {
              num: "⑤",
              label: "Anrede",
              desc: 'Immer persönlich — „Sehr geehrte Frau Müller" (nie „Sehr geehrte Damen und Herren")',
              color: "border-green-300 bg-green-50",
            },
            {
              num: "⑥",
              label: "Hauptteil",
              desc: "Text klar und übersichtlich gestalten — Leerzeile zwischen jedem Absatz",
              color: "border-purple-300 bg-purple-50",
            },
            {
              num: "⑦",
              label: "Schluss",
              desc: "Mit freundlichen Grüßen + Vorname Nachname + Anlagen",
              color: "border-gray-300 bg-gray-50",
            },
          ].map((item) => (
            <div
              key={item.num}
              className={`flex items-start gap-3 rounded-lg border-l-4 px-3 py-2.5 ${item.color}`}
            >
              <span className="text-base font-bold text-gray-600 flex-shrink-0">
                {item.num}
              </span>
              <div>
                <span className="text-sm font-bold text-gray-800">
                  {item.label} —{" "}
                </span>
                <span className="text-xs text-gray-600">{item.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </Accordion>

      {/* 03 */}
      <Accordion
        id="03"
        num="ABSCHNITT 03"
        icon="✍️"
        title="Der Hauptteil — Aufbau"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm font-semibold text-gray-700 mb-3">
          Strukturiere deinen Text klar mit Leerzeilen zwischen den Absätzen:
        </p>
        <div className="space-y-2">
          {(
            [
              {
                num: "1",
                label: "Einleitung",
                desc: "Der perfekte Hook — wecke sofort Neugier",
                cls: "bg-purple-100 text-purple-800 border-purple-300",
              },
              {
                num: "2",
                label: "Interesse für den Beruf",
                desc: "Warum genau dieser Beruf? Persönliche Faszination zeigen",
                cls: "bg-blue-100 text-blue-800 border-blue-300",
              },
              {
                num: "3",
                label: "Persönliche Stärken & Fähigkeiten",
                desc: "Konkrete Beispiele nach XYZ-Regel — kein leeres Behaupten",
                cls: "bg-green-100 text-green-800 border-green-300",
              },
              {
                num: "4",
                label: "Warum DIESES Unternehmen?",
                desc: "Zeige, dass du recherchiert hast — kein Copy-Paste!",
                cls: "bg-amber-100 text-amber-800 border-amber-300",
              },
              {
                num: "5",
                label: "Call to Action",
                desc: "Bitte klar um ein Vorstellungsgespräch + Erwähne Bangladesch-Herkunft",
                cls: "bg-red-100 text-red-800 border-red-300",
              },
            ] as { num: string; label: string; desc: string; cls: string }[]
          ).map((item) => (
            <div
              key={item.num}
              className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 ${item.cls}`}
            >
              <div className="w-6 h-6 rounded-full bg-white/60 flex items-center justify-center text-xs font-bold flex-shrink-0">
                {item.num}
              </div>
              <div>
                <p className="text-sm font-bold">{item.label}</p>
                <p className="text-xs opacity-80">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Accordion>

      {/* 04 */}
      <Accordion
        id="04"
        num="ABSCHNITT 04"
        icon="🪝"
        title={'Die Einleitung — Der perfekte „Hook"'}
        openId={openSection}
        onToggle={toggle}
      >
        <div className="mb-3 p-3 bg-red-50 border border-red-100 rounded-lg">
          <p className="text-sm text-red-700 italic">
            Vergiss Standardfloskeln wie{" "}
            <strong>„Hiermit bewerbe ich mich"</strong> — das liest jeder
            Recruiter hundert Mal.
          </p>
        </div>
        <div className="space-y-2 mb-4">
          {[
            {
              icon: "⚡",
              label: "Strategie",
              desc: "Wecke sofort Neugier mit einem starken, persönlichen Einstiegssatz",
            },
            {
              icon: "🔗",
              label: "Inhalt",
              desc: "Stelle einen direkten Bezug zur Ausbildung und zum Unternehmen her",
            },
            {
              icon: "💡",
              label: "Beispiel",
              desc: "Starte mit deiner Faszination für den Beruf oder einem aktuellen Bezug",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-start gap-2.5 p-3 rounded-lg bg-blue-50 border border-blue-100"
            >
              <span className="text-base flex-shrink-0">{item.icon}</span>
              <div>
                <span className="text-sm font-bold text-[#0369a1]">
                  {item.label}:{" "}
                </span>
                <span className="text-sm text-gray-700">{item.desc}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-xl bg-gray-900 p-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">
            ✕ Schlecht (Floskel):
          </p>
          <p className="text-xs text-red-400 italic mb-3">
            „Hiermit bewerbe ich mich um die Ausbildungsstelle..."
          </p>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">
            ✓ Gut (Hook):
          </p>
          <p className="text-xs text-green-400 italic">
            „Seit meiner Schulzeit fasziniert mich der Handel: Wie Produkte
            ihren Weg zum Kunden finden — und wie man dabei Vertrauen aufbaut.
            Genau deshalb bewerbe ich mich bei der Musterfirma GmbH."
          </p>
        </div>
      </Accordion>

      {/* 05 */}
      <Accordion
        id="05"
        num="ABSCHNITT 05"
        icon="🎭"
        title={"Stärken zeigen — „Show, don't tell\""}
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-3">
          Behauptungen ohne Belege überzeugen niemanden. Zeige deine Stärken
          durch konkrete Beispiele.
        </p>
        <div className="space-y-3">
          {[
            {
              icon: "🔍",
              label: "Beweise liefern",
              desc: "Nenne konkrete Beispiele für Soft Skills nach XYZ-Regel",
              example:
                "z.B. Teamfähigkeit im Sportverein · Zuverlässigkeit bei Schulprojekten",
              color: "bg-blue-50 border-blue-200",
            },
            {
              icon: "💻",
              label: "Fachliche Relevanz",
              desc: "Erwähne Praktika, Computerkenntnisse oder relevante Schulfächer",
              example:
                "z.B. MS Office · Buchhaltungs-Grundkenntnisse · Wirtschaft als Hauptfach",
              color: "bg-green-50 border-green-200",
            },
            {
              icon: "📊",
              label: "Wirkung zeigen (Impact)",
              desc: "Nutze wenn möglich Zahlen oder Ergebnisse, um deine Leistung greifbar zu machen",
              example:
                "z.B. GPA 3,8 · Note 1,5 in Buchhaltung · Projekterfolg mit 5-köpfigem Team",
              color: "bg-purple-50 border-purple-200",
            },
          ].map((item, i) => (
            <div key={i} className={`rounded-xl border p-3 ${item.color}`}>
              <div className="flex items-start gap-2 mb-1">
                <span className="text-base flex-shrink-0">{item.icon}</span>
                <p className="text-sm font-bold text-gray-800">{item.label}</p>
              </div>
              <p className="text-xs text-gray-600 pl-6 mb-1">{item.desc}</p>
              <p className="text-[11px] text-gray-400 italic pl-6">
                {item.example}
              </p>
            </div>
          ))}
        </div>
      </Accordion>

      {/* 06 */}
      <Accordion
        id="06"
        num="ABSCHNITT 06"
        icon="🏢"
        title="Warum DIESES Unternehmen?"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="mb-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
          <p className="text-sm text-amber-800 italic font-semibold">
            Kein Copy-Paste! Zeige, dass du dich informiert hast.
          </p>
        </div>
        <div className="space-y-2 mb-4">
          {[
            {
              icon: "🔎",
              label: "Recherche",
              desc: "Was gefällt dir an den Werten, Projekten oder Besonderheiten der Firma? Schau die Website an.",
            },
            {
              icon: "🔄",
              label: "Matching",
              desc: "Zeige, wie deine Ziele mit denen des Unternehmens übereinstimmen.",
            },
            {
              icon: "💡",
              label: "Tipp",
              desc: "Nenne den Firmennamen explizit im Text — das beweist individuelle Ansprache.",
              highlight: true,
            },
          ].map((item, i) => (
            <div
              key={i}
              className={`flex items-start gap-2.5 p-3 rounded-lg border ${item.highlight ? "bg-[#e0f2fe] border-[#009fe3]/40" : "bg-gray-50 border-gray-200"}`}
            >
              <span className="text-base flex-shrink-0">{item.icon}</span>
              <div>
                <span
                  className={`text-sm font-bold ${item.highlight ? "text-[#0369a1]" : "text-gray-800"}`}
                >
                  {item.label}:{" "}
                </span>
                <span className="text-sm text-gray-600">{item.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </Accordion>

      {/* 07 */}
      <Accordion
        id="07"
        num="ABSCHNITT 07"
        icon="📏"
        title="Wissenschaftliche Erfolgsfaktoren"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 italic mb-4">
          Halte dich an diese Richtwerte für maximale Lesbarkeit:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            {
              icon: "📝",
              value: "150–300",
              label: "Wörter — ideal",
              color: "bg-blue-50 border-blue-200 text-blue-800",
            },
            {
              icon: "⏱️",
              value: "~45 Sek.",
              label: "Lesezeit Recruiter",
              color: "bg-purple-50 border-purple-200 text-purple-800",
            },
            {
              icon: "💬",
              value: "Kurz!",
              label: "Prägnante Sätze",
              color: "bg-green-50 border-green-200 text-green-800",
            },
          ].map((item, i) => (
            <div
              key={i}
              className={`rounded-xl border p-3 text-center ${item.color}`}
            >
              <div className="text-2xl mb-1">{item.icon}</div>
              <div className="text-lg font-extrabold">{item.value}</div>
              <div className="text-xs font-medium mt-1 opacity-80">
                {item.label}
              </div>
            </div>
          ))}
        </div>
        <InfoBox variant="tip" icon="💡" title="Punchy Sentences">
          Nutze kurze, prägnante Sätze — sogenannte „Punchy sentences" — um den
          Leser wach zu halten. Ein Satz = ein Gedanke.
        </InfoBox>
      </Accordion>

      {/* 08 */}
      <Accordion
        id="08"
        num="ABSCHNITT 08"
        icon="⚖️"
        title="Dos & Don'ts"
        openId={openSection}
        onToggle={toggle}
      >
        <DosAndDonts />
      </Accordion>

      {/* 09 - Checkliste */}
      <Accordion
        id="09"
        num="ABSCHNITT 09"
        icon="✅"
        title="Checkliste vor dem Absenden"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-3">
          Bevor du absendest — alles geprüft?
        </p>
        <div className="space-y-2">
          {[
            {
              label: "Anrede",
              desc: 'Persönliche Anrede mit richtigem Namen (nie „Damen und Herren")',
              icon: "👤",
            },
            {
              label: "Struktur",
              desc: "Leerzeilen nach jedem Absatz gesetzt?",
              icon: "📐",
            },
            {
              label: "Länge",
              desc: "Text liegt zwischen 150 und 300 Wörtern?",
              icon: "📏",
            },
            {
              label: "Call to Action",
              desc: "Klar um Vorstellungsgespräch gebeten & erwähnt, dass du dich aus Bangladesch bewirbst?",
              icon: "📞",
            },
            {
              label: "Anlagen",
              desc: "Lebenslauf und Zeugnisse beigefügt? (Bei E-Mail-Bewerbung)",
              icon: "📎",
            },
            {
              label: "Korrektur",
              desc: "Von G2G Korrekturlesen lassen — vor dem Absenden!",
              icon: "🔍",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200"
            >
              <div className="w-5 h-5 rounded border-2 border-[#009fe3] flex-shrink-0 mt-0.5 bg-white" />
              <div>
                <span className="text-sm font-bold text-gray-800">
                  {item.icon} {item.label}:{" "}
                </span>
                <span className="text-xs text-gray-600">{item.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </Accordion>

      {/* 10 - Beispiel */}
      <Accordion
        id="10"
        num="ABSCHNITT 10"
        icon="📝"
        title="Beispiel — Anschreiben nach DIN 5008"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          So sieht ein vollständiges Anschreiben nach DIN 5008 aus — mit allen 7
          Bestandteilen:
        </p>
        <MockAnschreiben />
        <InfoBox variant="tip" icon="💡" title="Tipp">
          Passe alle Felder auf deine eigenen Daten und die jeweilige
          Stellenanzeige an. Lass das Anschreiben immer von G2G prüfen!
        </InfoBox>
      </Accordion>

      {/* Fazit */}
      <Accordion
        id="fazit"
        num="FAZIT"
        icon="🏁"
        title="Fazit"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <span className="text-2xl flex-shrink-0">✅</span>
          <div>
            <p className="text-sm font-bold text-green-800 mb-2">
              Ein gutes Anschreiben ist kurz, persönlich, beweisbar und
              individuell.
            </p>
            <ul className="space-y-1">
              {[
                "DIN 5008 — immer die gleiche formale Struktur",
                "Hook statt Floskel — erster Satz entscheidet",
                "XYZ-Regel für Stärken — zeigen, nicht behaupten",
                "Firmennamen nennen — zeigt individuelle Recherche",
                "Call to Action — klar um Gespräch bitten + Bangladesch erwähnen",
              ].map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-green-700 flex items-start gap-2"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Accordion>

      {/* Aufgaben */}
      <Accordion
        id="aufgaben"
        num="AUFGABEN"
        icon="📋"
        title="Deine Aufgaben – Lektion 04"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="pt-1">
          <TaskList
            tasks={[
              "1 passende Stellenanzeige aussuchen (aus den 3, die du in Lektion 03 ausgesucht hast)",
              "Ein vollständiges Anschreiben nach DIN 5008 schreiben (150–300 Wörter)",
              "Fokus auf B2-Sprachniveau sowie auf das Sprachzeugnis",
            ]}
          />
        </div>
      </Accordion>
    </div>
  );
};

export default Lecture04AnschreibenDIN5008;
